import { useUser, SignUpButton } from "@clerk/clerk-react";
import { ArrowRight } from "lucide-react";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  FootnoteList,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

const FAQ = [
  {
    q: "How does the NFC chip read on a phone with no app?",
    a: "Modern phones (iPhone 7 and later, every Android since 2017) include an NFC reader on the back of the device. When you hold a WhoBrew card within an inch, the reader picks up a small URL stored on the chip and the phone offers to open it in the browser. The same way Apple Pay works between a phone and a payment terminal, except this card is the terminal and your contact page is the payment.",
  },
  {
    q: "Do recipients need an app, an account, or anything else?",
    a: "No. Your card is a regular web page at whobrew.us/card/your-handle. The recipient sees your name, your title, your links, your calendar, and a contact form. They can save your details to their phone's contact app with one tap. Nothing else is required of them, ever.",
  },
  {
    q: "What happens if my phone is older than 2017 or NFC is disabled?",
    a: "Every WhoBrew card has a printed QR code on the back. Any phone with a camera can read it. The same URL opens, the same page renders, the same save-to-contacts works. NFC is the fast path; QR is the universal fallback.",
  },
];

const HOWTO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to set up an NFC business card with WhoBrew",
  description: "Create a digital card, add your links, share the URL or QR. Optional NFC card adds tap-to-open.",
  totalTime: "PT60S",
  step: [
    { "@type": "HowToStep", position: 1, name: "Claim your handle", text: "Pick the URL slug for your card." },
    { "@type": "HowToStep", position: 2, name: "Add your links",     text: "Drop in calendar, LinkedIn, portfolio, anything." },
    { "@type": "HowToStep", position: 3, name: "Share the URL",       text: "Text, email, AirDrop, or order an NFC card." },
    { "@type": "HowToStep", position: 4, name: "Tap with NFC",        text: "Hold the physical card to any modern phone." },
  ],
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function HowItWorks() {
  const { isSignedIn } = useUser();

  return (
    <>
      <SEO
        title="How NFC business cards work — WhoBrew"
        description="A 1,200-word explainer on NFC business cards: how the chip works, which phones support it, how to set yours up, and what happens when you change jobs."
        canonical={canonicalFor("/how-it-works")}
        jsonLd={[HOWTO_JSONLD, FAQ_JSONLD]}
      />

      {/* Hero */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose">
          <Eyebrow>How it works</Eyebrow>
          <Display as="h1" scale="lg" italicWord="actually" className="mt-5">
            How an NFC business card actually works.
          </Display>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            Most people have heard the phrase "NFC business card" and few have used one. Here is
            the technology, the workflow, the tradeoffs, and the answers to the questions people
            actually ask before they buy.
          </p>
        </Container>
      </Section>

      {/* Steps — numbered, vertical */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <div className="space-y-12">
            {[
              {
                step: 1,
                title: "Claim a handle.",
                body: "Pick the URL slug your card will live at. Most people use their name. Your card lives at whobrew.us/card/your-handle from the second you save. The handle is yours forever and the URL never changes — even if your job, your title, or your phone number does.",
              },
              {
                step: 2,
                title: "Drop in the links you already paste in DMs.",
                body: "LinkedIn, Calendly, portfolio, payment link, Substack, Spotify, GitHub, latest deck, sales playbook, real estate listing, the things people ask you for over and over. Reorder them by importance. Hide one for a single conversation, show it again later.",
              },
              {
                step: 3,
                title: "Share the URL the way you already share things.",
                body: "Text it. Email it. iMessage preview. WhatsApp link. Slack DM. AirDrop the page from Safari. The same URL opens the same page in every context. Anyone with a phone or a browser can read it. There is nothing for the recipient to install.",
              },
              {
                step: 4,
                title: "Tap with an NFC card, optional but lovely.",
                body: "Order a thin PVC card with your handle baked in. Hold it within an inch of any modern phone — iPhone 7 or later, every Android from the last eight years — and your card opens in the recipient's browser. The chip is the same kind that powers Apple Pay. The card has a printed QR on the back as a backup for older phones.",
              },
            ].map((s) => (
              <div key={s.step}>
                <Eyebrow step={s.step} of={4}>Step</Eyebrow>
                <Display as="h2" scale="sm" className="mt-3">
                  {s.title}
                </Display>
                <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Long-form: What is NFC */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>The technology</Eyebrow>
          <Display as="h2" scale="md" italicWord="NFC" className="mt-4 mb-8">
            What is NFC, in one paragraph?
          </Display>
          <div className="space-y-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            <p>
              NFC stands for Near Field Communication. It is a short-range radio standard that
              moves a small amount of data — usually a URL or a payment token — between two
              devices held within an inch of each other. It is the same technology that powers
              tap-to-pay at the grocery store and unlocks office buildings with a key card. It
              has shipped on every iPhone since 2014 and on most Android phones since 2012.
            </p>
            <p>
              An NFC business card has a chip the size of a thumbnail embedded in a thin sheet of
              PVC. The chip stores the URL of your WhoBrew page. When you hold the card near a
              phone, the phone's NFC reader picks up the URL, and the operating system asks the
              recipient if they want to open it. They tap once and your card is there, in the
              browser, scrollable, savable, and one tap away from being added to their contacts.
            </p>
            <p>
              No app on your end. No app on their end. No Bluetooth pairing, no account creation,
              no airdrop privacy panel. Just the link, opened by the most universal app on every
              phone — the browser.
            </p>
          </div>

          <Hairline className="my-12" />

          <Eyebrow>Compatibility</Eyebrow>
          <Display as="h2" scale="md" italicWord="phone" className="mt-4 mb-8">
            Which phones can read the card?
          </Display>
          <div className="space-y-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            <p>
              On the iPhone side: every model from the iPhone 7 onward reads NFC tags from the
              lock screen, no setup required. iPhone 6 and older have NFC but only for Apple Pay.
              For those, the recipient scans the printed QR on the back of the card.
            </p>
            <p>
              On the Android side: every Android from the last eight years (anything Lollipop or
              later) reads NFC out of the box. Some budget Android models from before 2017 omit
              the NFC chip entirely; the QR fallback covers them.
            </p>
            <p>
              In practice, the QR fallback is the safety net for less than two percent of phones
              in circulation. The remaining 98% of recipients tap and go.
            </p>
          </div>

          <Hairline className="my-12" />

          <Eyebrow>The job-change problem</Eyebrow>
          <Display as="h2" scale="md" italicWord="changes" className="mt-4 mb-8">
            What happens when your title changes?
          </Display>
          <div className="space-y-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            <p>
              The single greatest weakness of paper business cards is that they go stale the
              moment your job changes. You have boxes of cards listing the title you no longer
              hold and the phone number you no longer answer. They get handed to people who try
              to reach you a year later, find a dead number, and give up.
            </p>
            <p>
              A WhoBrew card has the same URL forever. You change the contents of the card from
              your phone, and every share you have ever made — at every conference, in every
              elevator, on every signed contract — points to the new version. The next person to
              tap the card you handed out in 2024 sees your 2026 title.
            </p>
            <p>
              This is the design intent. The card is the surface; the data behind it is liquid.
            </p>
          </div>

          <Hairline className="my-12" />

          <Eyebrow>Tradeoffs</Eyebrow>
          <Display as="h2" scale="md" italicWord="tradeoffs" className="mt-4 mb-8">
            The honest tradeoffs.
          </Display>
          <div className="space-y-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            <p>
              An NFC card needs a phone and an internet connection to render. A paper card does
              not. If you spend your career in basements with no signal, paper still wins. For
              everyone else, the moments when both you and your recipient are without phone
              signal are vanishingly rare.
            </p>
            <p>
              An NFC card costs more upfront — $14.99 for a single card vs. roughly $10 for a
              hundred paper cards from a print-on-demand service. The math reverses the moment
              you reorder paper cards: a single $14.99 card replaces every box you would ever
              print, forever.
            </p>
            <p>
              An NFC card is one card. You hand it to one person, then you put it back in your
              pocket. If you regularly hand out twenty cards in an evening, the workflow does
              change — but most professionals find that handing out one card per conversation,
              with full context, beats showering paper into a pile that gets thrown out at the
              end of the night.
            </p>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>The questions people ask</Eyebrow>
          <Display as="h2" scale="md" italicWord="actually" className="mt-4 mb-12">
            What we actually get asked.
          </Display>
          <div className="space-y-12">
            {FAQ.map((item, i) => (
              <div key={i}>
                <h3 className="font-display text-headline text-coffee-900 dark:text-cream-50 dark:text-cream-50">
                  {item.q}
                </h3>
                <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
          <FootnoteList notes={[
            { id: 1, text: "iPhone NFC reading from the lock screen launched with iOS 11, June 2017, available on iPhone 7 and later." },
            { id: 2, text: "Android NFC reading is part of the Android 5.0 (Lollipop) baseline released in 2014. Most Android phones since 2012 include NFC; budget exceptions exist." },
          ]} />
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="card" className="mx-auto max-w-3xl">
            Set up your card in 60 seconds.
          </Display>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                Open my card
              </Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                  Claim my handle
                </Button>
              </SignUpButton>
            )}
            <Button to="/order-card" variant="secondary" size="lg" onInk>
              Order an NFC card
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

export default HowItWorks;
